import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';

function getCurrentUserId(): number | null {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId ?? null;
  } catch {
    return null;
  }
}

interface PlanRecordRow {
  id?: number;
  article_id: number;
  product_id?: number | null;
  amount: number;
  quantity: number;
}

interface ArticleData {
  id: number;
  name: string;
  code?: string;
  is_income?: boolean;
  group_number?: string | null;
  number?: string | null;
}

interface ProductData {
  id: number;
  name: string;
  alias?: string | null;
}

interface CalculatedRow {
  key: string;
  articleId: number;
  productId: number | null;
  articleLabel: string;
  productLabel: string;
  baseQty: number;
  baseAmount: number;
  correction: number;
  newQty: number;
  newAmount: number;
  isIncome: boolean;
  groupNumber: string;
  number: string;
}

interface Props {
  targetMonth: string;
  onClose: () => void;
  onSaved: () => void;
}

const PlanFillModal: React.FC<Props> = ({ targetMonth, onClose, onSaved }) => {
  const [baseMonth, setBaseMonth] = useState('');
  const [correction, setCorrection] = useState('0');
  const [rows, setRows] = useState<CalculatedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [articles, setArticles] = useState<ArticleData[]>([]);
  const [products, setProducts] = useState<ProductData[]>([]);

  useEffect(() => {
    const fetchRefs = async () => {
      const [articleRes, productRes] = await Promise.all([
        apiFetch('/api/articles'),
        apiFetch('/api/products'),
      ]);
      const articleJson = articleRes.ok ? await articleRes.json() : [];
      const productJson = productRes.ok ? await productRes.json() : [];
      setArticles(Array.isArray(articleJson) ? articleJson : []);
      setProducts(Array.isArray(productJson) ? productJson : []);
    };
    fetchRefs().catch(() => setArticles([]));
  }, []);

  useEffect(() => {
    const base = targetMonth
      ? new Date(Number(targetMonth.split('-')[0]), Number(targetMonth.split('-')[1]) - 1, 1)
      : new Date();
    base.setMonth(base.getMonth() - 1);
    const value = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
    setBaseMonth(value);
  }, [targetMonth]);

  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const base = targetMonth
      ? new Date(Number(targetMonth.split('-')[0]), Number(targetMonth.split('-')[1]) - 1, 1)
      : new Date();
    for (let i = 1; i <= 24; i += 1) {
      const d = new Date(base);
      d.setMonth(d.getMonth() - i);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  }, [targetMonth]);

  function formatNumber(value: number) {
    const hasFraction = Math.abs(value % 1) > 0;
    const fractionDigits = hasFraction ? 2 : 0;
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  function parseCorrection(value: string) {
    if (!value) return 0;
    const normalized = value.replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function getMonthDays(value: string) {
    const [yearStr, monthStr] = value.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (!year || !month) return 0;
    return new Date(year, month, 0).getDate();
  }

  function buildLabel(article?: ArticleData) {
    if (!article) return '';
    return `${article.code ?? ''} ${article.name ?? ''}`.trim();
  }

  function buildProductLabel(product?: ProductData) {
    if (!product) return '';
    if (product.alias && product.alias.trim()) return product.alias;
    return product.name ?? '';
  }

  const compareArticle = (a: CalculatedRow, b: CalculatedRow) => {
    const groupCmp = a.groupNumber.localeCompare(b.groupNumber, 'ru', { numeric: true });
    if (groupCmp !== 0) return groupCmp;
    return a.number.localeCompare(b.number, 'ru', { numeric: true });
  };

  useEffect(() => {
    if (!rows.length) return;
    const articleMap = new Map(articles.map(article => [article.id, article]));
    const productMap = new Map(products.map(product => [product.id, product]));
    setRows(prevRows => {
      const updated = prevRows.map(row => {
        const article = articleMap.get(row.articleId);
        const product = row.productId ? productMap.get(row.productId) : undefined;
        return {
          ...row,
          articleLabel: buildLabel(article) || row.articleLabel,
          productLabel: buildProductLabel(product) || row.productLabel,
          isIncome: article?.is_income ?? row.isIncome,
          groupNumber: String(article?.group_number ?? row.groupNumber ?? ''),
          number: String(article?.number ?? row.number ?? ''),
        };
      });
      const incomeRows = updated.filter(r => r.isIncome).sort(compareArticle);
      const expenseRows = updated.filter(r => !r.isIncome).sort(compareArticle);
      return [...incomeRows, ...expenseRows];
    });
  }, [articles, products]);

  async function handleCalculate() {
    if (!baseMonth) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/plan-records?month=${baseMonth}&aggregate=1`);
      if (!res.ok) {
        setError('Не удалось получить плановые записи.');
        setRows([]);
        return;
      }
      const data = (await res.json()) as PlanRecordRow[];
      const correctionValue = parseCorrection(correction);
      const factor = 1 + correctionValue / 100;
      const baseDays = getMonthDays(baseMonth);
      const targetDays = getMonthDays(targetMonth || baseMonth);
      if (!baseDays || !targetDays) {
        setError('Некорректный месяц для расчета.');
        setRows([]);
        return;
      }
      const mapped = (Array.isArray(data) ? data : []).map(row => {
        const article = articles.find(a => a.id === row.article_id);
        const product = products.find(p => p.id === row.product_id);
        const totalQty = Number(row.quantity ?? 0);
        const totalAmount = Number(row.amount ?? 0);
        const baseQty = totalQty / baseDays;
        const baseAmount = totalAmount / baseDays;
        const newQty = Math.round(baseQty * factor * targetDays);
        const newAmount = Number((baseAmount * factor * targetDays).toFixed(2));
        return {
          key: `${row.article_id}-${row.product_id ?? 'none'}`,
          articleId: row.article_id,
          productId: row.product_id ?? null,
          articleLabel: buildLabel(article) || `ID ${row.article_id}`,
          productLabel: buildProductLabel(product) || (row.product_id ? `ID ${row.product_id}` : ''),
          baseQty,
          baseAmount,
          correction: correctionValue,
          newQty,
          newAmount,
          isIncome: article?.is_income ?? false,
          groupNumber: String(article?.group_number ?? ''),
          number: String(article?.number ?? ''),
        };
      });
      const incomeRows = mapped.filter(r => r.isIncome).sort(compareArticle);
      const expenseRows = mapped.filter(r => !r.isIncome).sort(compareArticle);
      setRows([...incomeRows, ...expenseRows]);
    } catch {
      setError('Ошибка при расчете.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!rows.length) return;
    if (!targetMonth) {
      setError('Не выбран месяц, в который нужно сохранить записи.');
      return;
    }
    setSaving(true);
    setError(null);
    const uid = getCurrentUserId();
    const payloads = rows.map(row => ({
      date_month_end: `${targetMonth}-01`,
      article_id: row.articleId,
      product_id: row.productId,
      amount: row.newAmount,
      quantity: row.newQty,
      comment: '',
      ...(uid !== null ? { owner_id: uid } : {}),
    }));
    try {
      await Promise.all(
        payloads.map(payload =>
          apiFetch('/api/plan-records', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        )
      );
      onSaved();
    } catch {
      setError('Ошибка при сохранении.');
    } finally {
      setSaving(false);
    }
  }

  const correctionValue = parseCorrection(correction);
  const incomeTotal = rows.filter(r => r.isIncome).reduce((sum, r) => sum + r.newAmount, 0);
  const expenseTotal = rows.filter(r => !r.isIncome).reduce((sum, r) => sum + r.newAmount, 0);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const confirmed = confirm(
      'Вы уверены, что хотите закрыть окно? Данные не сохранятся.'
    );
    if (confirmed) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-5xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold text-slate-800">
          Заполнить на основании
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Месяц (основание)
            </label>
            <select
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={baseMonth}
              onChange={e => setBaseMonth(e.target.value)}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Коррекция, %
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={correction}
              onChange={e => setCorrection(e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleCalculate}
              disabled={loading}
            >
              {loading ? 'Рассчитываем...' : 'Рассчитать'}
            </button>
            <button
              className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSave}
              disabled={saving || rows.length === 0}
            >
              {saving ? 'Сохраняем...' : 'Сохранить'}
            </button>
          </div>
        </div>
        <div className="mt-2 text-xs text-slate-500">
          Заполнение будет выполнено для месяца: {targetMonth || 'не выбран'}
        </div>
        {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}
        <div className="mt-4 overflow-auto rounded-xl border border-slate-200">
          <table className="min-w-full border-collapse text-sm text-slate-700">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Статья</th>
                <th className="px-3 py-2 text-left">Товар</th>
                <th className="px-3 py-2 text-center">Было количество / было сумма</th>
                <th className="px-3 py-2 text-center">Коррекция</th>
                <th className="px-3 py-2 text-center">Количество</th>
                <th className="px-3 py-2 text-center">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">
                    Нет данных для расчета.
                  </td>
                </tr>
              )}
              {rows.filter(r => r.isIncome).map(row => (
                <tr key={`income-${row.key}`} className="border-t border-slate-100">
                  <td className="px-3 py-2">{row.articleLabel}</td>
                  <td className="px-3 py-2">{row.productLabel}</td>
                  <td className="px-3 py-2 text-center">
                    {formatNumber(row.baseQty)} / {formatNumber(row.baseAmount)}
                  </td>
                  <td className="px-3 py-2 text-center">{formatNumber(correctionValue)}%</td>
                  <td className="px-3 py-2 text-center">{formatNumber(row.newQty)}</td>
                  <td className="px-3 py-2 text-center">{formatNumber(row.newAmount)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                <td className="px-3 py-2" colSpan={5}>
                  Приход
                </td>
                <td className="px-3 py-2 text-center">{formatNumber(incomeTotal)}</td>
              </tr>
              {rows.filter(r => !r.isIncome).map(row => (
                <tr key={`expense-${row.key}`} className="border-t border-slate-100">
                  <td className="px-3 py-2">{row.articleLabel}</td>
                  <td className="px-3 py-2">{row.productLabel}</td>
                  <td className="px-3 py-2 text-center">
                    {formatNumber(row.baseQty)} / {formatNumber(row.baseAmount)}
                  </td>
                  <td className="px-3 py-2 text-center">{formatNumber(correctionValue)}%</td>
                  <td className="px-3 py-2 text-center">{formatNumber(row.newQty)}</td>
                  <td className="px-3 py-2 text-center">{formatNumber(row.newAmount)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                <td className="px-3 py-2" colSpan={5}>
                  Расход
                </td>
                <td className="px-3 py-2 text-center">{formatNumber(expenseTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanFillModal;
